import { Box, Flex, Heading, useToken, Text, VStack } from '@chakra-ui/react';
import { useState, useCallback, useEffect } from 'react';
import { _js } from '@ifixit/localize';
import { FaIcon } from '@ifixit/icons';
import {
   faScrewdriverWrench as faScrewdriverWrenchDuotone,
   faPenToSquare as faPenToSquareDuotone,
   faCertificate as faCertificateDuotone,
} from '@fortawesome/pro-duotone-svg-icons';
import { initializeChakraComponent } from 'Shared/chakra-initialize';
import { SentryError } from '@ifixit/sentry';
import { RegisterCode } from './RegisterCode';
import { Email } from './Email';
import { LoginCode } from './LoginCode';
import { Auth } from 'Shared/auth';
import type { FlowStep, AuthInstance, AuthResponse } from './types';

interface JoinPageProps {
   privacyPolicyUrl: string;
}

export const JoinPage = ({ privacyPolicyUrl }: JoinPageProps) => {
   const gray50 = useToken('colors', 'gray.50');
   const [flowStep, setFlowStep] = useState<FlowStep>('email');
   const [userEmail, setUserEmail] = useState<string>('');

   // Set body background color
   useEffect(() => {
      const originalBackgroundColor = document.body.style.backgroundColor;
      document.body.style.backgroundColor = gray50;

      return () => {
         document.body.style.backgroundColor = originalBackgroundColor;
      };
   }, [gray50]);

   const handleNavigateToLogin = useCallback(async (email: string) => {
      try {
         const auth = Auth as unknown as AuthInstance;
         await new Promise<void>((resolve, reject) => {
            void auth.sendGetLoginCode({ login: email }, (response: AuthResponse) => {
               if (response.loggedIn === false && response.reason) {
                  reject(new SentryError(`Login code request failed: ${response.reason}`));
               } else {
                  resolve();
               }
            });
         });
         setUserEmail(email);
         setFlowStep('login');
      } catch (error) {
         console.error('Failed to send login code:', error);
      }
   }, []);

   const handleNavigateToRegister = useCallback(async (email: string) => {
      try {
         const auth = Auth as unknown as AuthInstance;
         await new Promise<void>((resolve, reject) => {
            void auth.sendGetRegisterCode({ login: email }, (response: AuthResponse) => {
               if (response.loggedIn === false && response.reason) {
                  reject(new SentryError(`Register code request failed: ${response.reason}`));
               } else {
                  resolve();
               }
            });
         });
         setUserEmail(email);
         setFlowStep('register');
      } catch (error) {
         console.error('Failed to send register code:', error);
      }
   }, []);

   const handleBackToEmail = useCallback(() => {
      setFlowStep('email');
   }, []);

   return (
      <>
         <Flex
            gap={8}
            paddingY={8}
            marginY="auto"
            wrap="wrap"
            justify="center"
            align="center"
            minHeight="60vh"
            data-testid="loginContainer"
         >
            <JoinLeftContent />
            <VStack width={{ base: '100%', lg: 'auto' }}>
               {flowStep === 'email' && (
                  <Email
                     onNavigateToLogin={handleNavigateToLogin}
                     onNavigateToRegister={handleNavigateToRegister}
                  />
               )}
               {flowStep === 'login' && (
                  <LoginCode email={userEmail} onNavigateBack={handleBackToEmail} />
               )}
               {flowStep === 'register' && (
                  <RegisterCode email={userEmail} privacyPolicyUrl={privacyPolicyUrl} />
               )}
            </VStack>
         </Flex>
         {(window as any).App?.dataProtectionMessage && (
            <Text
               dangerouslySetInnerHTML={{
                  __html: (window as any).App.dataProtectionMessage,
               }}
            />
         )}
      </>
   );
};

const JoinLeftContent = () => {
   const duotoneIconStyles = {
      boxSize: { base: '20px', sm: '24px' },
      color: 'brand.500',
      marginBlock: 'auto',
      marginRight: 2,
   };

   return (
      <Box fontSize="16px" width={{ base: '100%', md: '416px' }} marginBlock="auto">
         <Heading
            as="h2"
            fontSize={{ base: '24px', sm: '30px' }}
            fontWeight="medium"
            border="none"
            mt="0"
            lineHeight="normal"
         >
            {_js("Join the world's largest repair community")}
         </Heading>
         <VStack marginTop={6} spacing={4} align="flex-start">
            <Flex>
               <FaIcon icon={faScrewdriverWrenchDuotone} {...duotoneIconStyles} />
               {_js('Learn from 2.5 million like-minded fixers')}
            </Flex>
            <Flex>
               <FaIcon icon={faPenToSquareDuotone} {...duotoneIconStyles} />
               {_js('Share your repair expertise with step-by-step guides')}
            </Flex>
            <Flex>
               <FaIcon icon={faCertificateDuotone} {...duotoneIconStyles} />
               {_js('Participate to earn reputation and help fix the world')}
            </Flex>
         </VStack>
      </Box>
   );
};

initializeChakraComponent('JoinPage', JoinPage);
